/**
 * 分享对话框（参考电商复刻视觉语言）
 *
 * - 仅当项目存在已完成成片时启用
 * - 创建分享会自动停用旧链接（每项目同时只有 1 条有效）
 * - 含密码 / 有效期 / 是否允许下载
 */

import { useState } from 'react';
import { Loader2, Share2, X, Copy, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError } from '../../services/directorApi';

interface Props {
  open: boolean;
  projectId: string;
  hasCompletedComposition: boolean;
  onClose: () => void;
}

const EXPIRES_OPTIONS: Array<{ value: 'forever' | number; label: string }> = [
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
  { value: 180, label: '180 天' },
  { value: 'forever', label: '永久' },
];

export function ShareDialog({ open, projectId, hasCompletedComposition, onClose }: Props) {
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState<'forever' | number>(30);
  const [submitting, setSubmitting] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleCreate = async () => {
    if (!hasCompletedComposition) {
      toast.error('项目还没有已完成的成片');
      return;
    }
    if (usePassword && !/^\d{4,6}$/.test(password)) {
      toast.error('密码必须是 4-6 位数字');
      return;
    }
    setSubmitting(true);
    try {
      const result = await directorApi.createShare(projectId, {
        password: usePassword ? password : undefined,
        expiresInDays,
        allowDownload,
      });
      setShareUrl(result.shareUrl);
      toast.success('分享链接已创建');
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleRevoke = async () => {
    if (!window.confirm('停用后链接立即失效，访客无法继续访问。继续吗？')) return;
    setSubmitting(true);
    try {
      await directorApi.revokeShare(projectId);
      toast.success('分享链接已停用');
      setShareUrl(null);
      onClose();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '停用失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                分享成片
              </div>
              <h2 className="text-lg font-semibold leading-tight">把你的短剧分享给朋友</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 bg-slate-50 p-5">
          {!hasCompletedComposition && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">尚未生成成片</div>
                <div className="mt-0.5 text-xs">需要先在"预览成片"里完成合成才能分享</div>
              </div>
            </div>
          )}

          {/* 已有链接 */}
          {shareUrl ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  分享链接
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none focus:border-sky-300"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-emerald-600" /> 已复制
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" /> 复制
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-white p-3 text-xs leading-5 text-slate-500 ring-1 ring-slate-100">
                把链接发给朋友看，无需登录即可访问。每个项目同时只有 1 条有效链接，再次创建会停用旧的。
              </div>

              <button
                type="button"
                onClick={handleRevoke}
                disabled={submitting}
                className="w-full rounded-2xl border border-rose-200 bg-white py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
              >
                停用此链接
              </button>
            </div>
          ) : (
            <>
              {/* 密码 */}
              <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
                <label className="mb-1.5 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={usePassword}
                    onChange={(e) => setUsePassword(e.target.checked)}
                    className="accent-sky-500"
                  />
                  <span className="text-sm font-semibold text-slate-900">
                    设置访问密码（可选）
                  </span>
                </label>
                {usePassword && (
                  <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="4-6 位数字"
                    maxLength={6}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm tracking-widest text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300"
                  />
                )}
              </div>

              {/* 有效期 */}
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  有效期
                </label>
                <div className="grid grid-cols-4 gap-1.5">
                  {EXPIRES_OPTIONS.map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setExpiresInDays(opt.value)}
                      className={`rounded-2xl border py-2 text-sm font-semibold transition ${
                        expiresInDays === opt.value
                          ? 'border-sky-500 bg-sky-50 text-sky-700 shadow-sm shadow-sky-100'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-sky-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 是否允许下载 */}
              <label className="flex cursor-pointer items-center gap-2 rounded-2xl bg-white p-3 ring-1 ring-slate-100">
                <input
                  type="checkbox"
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                  className="accent-sky-500"
                />
                <span className="text-sm text-slate-900">允许访客下载 mp4</span>
              </label>
            </>
          )}
        </div>

        {!shareUrl && (
          <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={
                submitting ||
                !hasCompletedComposition ||
                (usePassword && password.length < 4)
              }
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              创建链接
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
